import { FC } from 'react';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { ButtonPrimary, Form, InputText } from '@lambdacurry/component-library';
import { Card, CardContent, Grid } from 'lib/components';
import { useRegisterMutation } from 'lib/home/generated/queries';
import { confirmPasswordInitialValues } from 'lib/forms/initialValues';
import { confirmPasswordValidation, emailAddressValidation } from 'lib/forms/validation';
import { RegistrationRouteMap, RegistrationStep, useAuthStore, useRegistrationStore } from '../../store';
import {
  GettingStartedLayout,
  GettingStartedNoCookingFooter,
  GettingStartedContent,
  ConfirmPasswordInputGroup,
  InputGrid,
  AuthButtons
} from '../../components';
import { default as LoginPattern } from '../../assets/login-pattern.svg';

interface FormValues {
  email: string;
  new_password: string;
  confirm_password: string;
  postal_code: string;
}

const CreateAccountPage: FC = () => {
  const { setCheckoutInfo, checkoutInfo, preferences, selectedPlan, promoCode, referralCode } = useRegistrationStore();
  const { token } = useAuthStore();
  const { mutate } = useRegisterMutation({
    onSuccess: ({ register: { hashed_password, JWT } }, { createUserInput: { email } }) => {
      setCheckoutInfo({ email, postal_code, hashed_password });
      navigate(RegistrationRouteMap.checkEmail, { state: { JWT } });
    },
    onError: error => {
      if (error.toString().includes('already exists')) navigate('/login');
    }
  });

  const postal_code = checkoutInfo?.postal_code;
  const initialValues = { email: '', postal_code, ...confirmPasswordInitialValues };
  const validationSchema = Yup.object({
    ...emailAddressValidation,
    ...confirmPasswordValidation
  });

  const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    mutate(
      {
        createUserInput: {
          ...checkoutInfo,
          ...values,
          meal_preferences: preferences,
          selectedPlan,
          promo_code: promoCode,
          referral_code: referralCode
        }
      },
      {
        onSuccess: data => {
          window.dataLayer.push({ event: 'register' });
        }
      }
    );
  };

  if (token) {
    navigate(RegistrationRouteMap.customizeBox);
    return null;
  }

  return (
    <GettingStartedLayout step={RegistrationStep.createAccount} className="getting-started-create-account">
      <GettingStartedContent>
        <Grid>
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Card className="relative">
              <div className="md:flex">
                <CardContent className="my-8 md:my-0">
                  <h2 className="mb-8">Let's Create an Account</h2>
                  <AuthButtons redirect={RegistrationRouteMap.customizeBox} />

                  <div className="mt-4 mb-4 text-center">or</div>

                  <Form<FormValues>
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                  >
                    {formikProps => (
                      <>
                        <InputGrid>
                          <InputText label="Email" placeholder="example@email.com" type="email" name="email" />
                          <ConfirmPasswordInputGroup />
                        </InputGrid>

                        <ButtonPrimary className="mt-6 md:w-56" type="submit" disabled={formikProps.isSubmitting}>
                          Continue
                        </ButtonPrimary>
                      </>
                    )}
                  </Form>
                </CardContent>
                <CardContent className="bg-brand-green-50 min-h-[132px] md:min-h-[500px] md:max-w-md md:min-w-[200px] pr-24 md:pr-0 !border-0" />
              </div>
              <StaticImage
                className="!absolute bottom-0 right-5 !hidden md:!block h-48 md:h-[500px]"
                src="../../assets/sage.png"
                alt="hand holding herbs"
                placeholder="tracedSVG"
                height={500}
                quality={100}
              />
              <StaticImage
                className="!absolute bottom-0 right-5 md:!hidden"
                src="../../assets/sage.png"
                alt="hand holding herbs"
                placeholder="tracedSVG"
                height={192}
              />
              <LoginPattern className="h-auto w-[185.85%] sm:w-[118.05%] md:w-full bg-brand-green-50" />
            </Card>
          </div>
        </Grid>
      </GettingStartedContent>

      <GettingStartedNoCookingFooter />
    </GettingStartedLayout>
  );
};

export default CreateAccountPage;
